<template>
  <div>
    <div
      :class="[
        'carousel-two',
        props.componentData.componentCode === 'CAROUSEL_2'
          ? 'carousel-dots'
          : 'carousel-bars',
      ]"
      :style="{
        height: `${configDataForm.imageHeight}px`,
      }"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
    >
      <el-carousel
        ref="carouselRef"
        :interval="5000"
        :height="'100%'"
        arrow="always"
        @change="handleCarouselChange"
      >
        <el-carousel-item
          v-for="item in configDataForm.items"
          :key="item"
          :name="item.backgroundFileName"
        >
          <a :href="item.linkUrl" target="_blank">
            <el-image
              style="width: 100%; height: 100%"
              :src="item.backgroundFileFullUrl || noUpload"
              fit="cover"
            ></el-image>
          </a>
        </el-carousel-item>
      </el-carousel>
      <operation-container
        v-if="showOperationBtn && !showEditForm"
        :component-data="props.componentData"
        :show-edit-content-button="props.showEditContentButton"
        :show-edit-components-button="props.showEditComponentsButton"
        :show-delete-components-button="props.showDeleteComponentsButton"
        :show-insert-components-button="props.showInsertComponentsButton"
        :show-sort-components-button="props.showSortComponentsButton"
        @handle-show-edit-form="handleShowEditForm"
        @handle-change-components="handleChangeComponents"
        @handle-delete-components="handleDeleteComponents"
        @handle-insert-components="handleInsertComponents"
        @handle-sort-components="handleSortComponents"
      ></operation-container>
    </div>
    <el-drawer
      v-if="showEditForm"
      v-model="showEditForm"
      size="60%"
      :title="props.componentData.componentName"
    >
      <carousel-edit-form
        v-if="showEditForm"
        v-model="configDataForm"
        :component-data="props.componentData"
        @handle-show-edit-form="handleShowEditForm"
        @handle-edit-components-content="handleEditComponentsContent"
      ></carousel-edit-form>
    </el-drawer>
  </div>
</template>
<script setup>
// #region 引用
import OperationContainer from '../common/OperationContainer.vue';
import CarouselEditForm from './CarouselEditForm.vue';
// #endregion

// #region props/emit
/**
 * 属性
 */
const props = defineProps({
  showEditContentButton: {
    type: Boolean,
    default: true,
  },
  showEditComponentsButton: {
    type: Boolean,
    default: false,
  },
  showDeleteComponentsButton: {
    type: Boolean,
    default: false,
  },
  showInsertComponentsButton: {
    type: Boolean,
    default: false,
  },
  showSortComponentsButton: {
    type: Boolean,
    default: false,
  },
  componentData: {
    type: Object,
    default() {},
  },
});

/**
 * 方法
 */
const emits = defineEmits([
  'handle-show-edit-form',
  'handle-change-components',
  'handle-delete-components',
  'handle-insert-components',
  'handle-sort-components',
  'handle-edit-components-content',
  'update:modelValue',
]);

// #endregion

// #region 变量/常量

// 处理图片未上传的情况
// 预加载所有图片（构建时生成映射）
const images = import.meta.glob('@/assets/images/web/common/other/*.png', {
  eager: true,
  query: '?url',
});
// 直接获取目标图片 URL
const noUpload =
  images['/src/assets/images/web/common/other/no-upload.png']?.default;

const configDataForm = ref({
  items: [
    {
      sortNum: 1,
      backgroundType: 1,
      backgroundFileName: '示例图片1.jpg',
      backgroundFileKey: 'background1',
      linkType: 2,
      linkUrl: 'https://www.baidu.com',
    },
    {
      sortNum: 2,
      backgroundType: 1,
      backgroundFileName: '示例图片2.jpg',
      backgroundFileKey: 'background2',
      linkType: 1,
      linkUrl: '/article-list/132',
    },
  ],
});

if (props.componentData.configData) {
  const parsed = JSON.parse(props.componentData.configData);
  configDataForm.value = { ...configDataForm.value, ...parsed };
  if (configDataForm.value.items) {
    configDataForm.value.items.sort((a, b) => {
      return a.sortNum - b.sortNum;
    });
  }
}
// 操作显示按钮控制
const showOperationBtn = ref(false);
// 显示编辑表单
const showEditForm = ref(false);

// 轮播图控制
const carouselRef = ref(null);
const currentIndex = ref(0);
const isPaused = ref(false);

// 切换到指定幻灯片
const handleSwitchSlide = (index) => {
  if (carouselRef.value) {
    carouselRef.value.setActiveItem(index);
  }
};

// 监听轮播切换
const handleCarouselChange = (current) => {
  currentIndex.value = current;
};

// 鼠标悬停暂停动画
const handleMouseEnter = () => {
  isPaused.value = true;
  handleShowOperationBtn(props.showEditContentButton);
};

const handleMouseLeave = () => {
  isPaused.value = false;
  handleShowOperationBtn(false);
};

// #endregion

// #region 生命周期

// #endregion

// #region 业务方法

/**
 * 显示编辑表单
 */
const handleShowEditForm = (flag) => {
  showEditForm.value = flag;
};
/**
 * 处理编辑表单内容
 */
const handleEditComponentsContent = (flag) => {
  // 待数据更新完成后才执行
  nextTick(() => {
    emits('update:modelValue', configDataForm.value);
    emits('handle-edit-components-content', true);
  });

  handleShowEditForm(flag);
};

/**
 * 显示
 */
const handleShowOperationBtn = (flag) => {
  showOperationBtn.value = flag;
};

/**
 * 改变组件
 */
const handleChangeComponents = () => {
  emits('handle-change-components', true);
};

/**
 * 删除组件
 */
const handleDeleteComponents = () => {
  emits('handle-delete-components', true);
};

/**
 * 插入组件
 */
const handleInsertComponents = () => {
  emits('handle-insert-components');
};
/**
 * 组件排序
 */
const handleSortComponents = (moveObj) => {
  emits('handle-sort-components', moveObj);
};

// #endregion
</script>
<style scoped lang="scss">
@import '@/styles/website/_variables.scss';

// ============================================
// 轮播图组件变量（继承官网设计系统）
// ============================================
$carousel-color-primary: $website-primary; // 主色调（深蓝）
$carousel-color-accent: $website-primary-light; // 强调色（亮青）
$carousel-color-text-light: #fff; // 浅色文字

// 动画时长
$carousel-duration-fast: $website-duration-fast;
$carousel-duration-normal: $website-duration-normal;
$carousel-duration-slow: $website-duration-slow;

// 动画曲线
$carousel-ease-out: $website-ease-out;
$carousel-ease-in-out: $website-ease-in-out;

/* 在屏幕宽度大于等于 1180px 时应用以下样式 */

/* PC端样式 */
@include pc {
  .carousel-two {
    position: relative;
    width: 100%;
    height: calc(100vh - 1px) !important;
    min-height: 450px !important;
    margin-top: 0;
    overflow: visible;

    .el-carousel {
      position: relative;
      width: 100%;
      height: 100% !important;
      margin: auto;
    }

    :deep(.el-carousel__container) {
      height: 100% !important;
    }

    // ========== 轮播项样式 ==========
    :deep(.el-carousel__item) {
      overflow: hidden;
    }

    // ========== 箭头按钮样式 ==========
    :deep(.el-carousel__arrow) {
      backdrop-filter: blur(4px);
      box-shadow: 0 4px 16px rgb(0 0 0 / 20%);
      transition: all $carousel-duration-normal $carousel-ease-out;

      .el-icon {
        font-size: 20px;
        color: $carousel-color-text-light;
        transition: color $carousel-duration-fast;
      }
    }

    :deep(.el-carousel__arrow--left) {
      left: 40px;
    }

    :deep(.el-carousel__arrow--right) {
      right: 40px;
    }

    // CAROUSEL_3 箭头：60×60 圆形
    &.carousel-bars {
      :deep(.el-carousel__arrow) {
        width: 60px;
        height: 60px;
        background: rgb(0 0 0 / 30%);
        border-radius: 50%;

        &:hover {
          background: rgb(0 0 0 / 50%);
        }
      }
    }

    // CAROUSEL_2 箭头：46×60 圆角矩形
    &.carousel-dots {
      :deep(.el-carousel__arrow) {
        width: 46px;
        height: 60px;
        background: rgb(0 0 0 / 30%);
        border-radius: $website-radius-md;

        &:hover {
          background: rgb(0 0 0 / 50%);
        }
      }
    }

    // ========== 指示器样式：细长条（CAROUSEL_3 默认） ==========
    &.carousel-bars {
      :deep(.el-carousel__indicators) {
        bottom: 10px;

        .el-carousel__indicator {
          margin: 0 8px;

          .el-carousel__button {
            width: 40px;
            height: 3px;
            background: rgb(255 255 255 / 30%);
            border-radius: 2px;
            transition: all $carousel-duration-normal $carousel-ease-out;
          }

          &.is-active .el-carousel__button {
            width: 60px;
            height: 4px;
            background: #fff;
          }

          &:hover .el-carousel__button {
            background: rgb(255 255 255 / 50%);
          }

          &.is-active:hover .el-carousel__button {
            background: #fff;
          }
        }
      }
    }

    // ========== 指示器样式：小圆点（CAROUSEL_2） ==========
    &.carousel-dots {
      :deep(.el-carousel__indicators) {
        bottom: 10px;

        .el-carousel__indicator {
          margin: 0 6px;

          .el-carousel__button {
            width: 10px;
            height: 10px;
            background: rgb(255 255 255 / 40%);
            border-radius: 50%;
            transition: all $carousel-duration-normal $carousel-ease-out;
          }

          &.is-active .el-carousel__button {
            width: 10px;
            height: 10px;
            background: #fff;
          }

          &:hover .el-carousel__button {
            background: rgb(255 255 255 / 60%);
          }

          &.is-active:hover .el-carousel__button {
            background: #fff;
          }
        }
      }
    }

    // 占位背景色
    .el-carousel__item:nth-child(2n) {
      background-color: #f0f2f5;
    }

    .el-carousel__item:nth-child(2n + 1) {
      background-color: #e8eaed;
    }
  }
}

/* 移动端样式 */
@include mobile {
  .carousel-two {
    position: relative;
    width: 100vw;
    max-width: 100vw;
    height: 32vh !important;
    margin-left: calc(50% - 50vw);
    overflow: hidden;

    .el-carousel {
      position: relative;
      width: 100%;
      height: 32vh;
      margin: auto;
    }

    :deep(.el-carousel__item) {
      overflow: hidden;
    }

    :deep(.el-image__inner) {
      object-fit: cover !important;
      transition: transform $carousel-duration-slow $carousel-ease-out;
    }

    // 移动端箭头
    :deep(.el-carousel__arrow) {
      backdrop-filter: blur(8px);
      box-shadow: 0 2px 12px rgb(0 0 0 / 25%);
      transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);

      &:active {
        transform: scale(0.95);

        .el-icon {
          color: $carousel-color-text-light;
        }
      }

      .el-icon {
        font-size: 18px;
        color: $carousel-color-text-light;
      }
    }

    :deep(.el-carousel__arrow--left) {
      left: 3vw;
    }

    :deep(.el-carousel__arrow--right) {
      right: 3vw;
    }

    // CAROUSEL_3 移动端箭头：30×30 圆形
    &.carousel-bars {
      :deep(.el-carousel__arrow) {
        width: 30px;
        height: 30px;
        background: rgb(0 0 0 / 35%);
        border-radius: 50%;

        &:active {
          background: rgb(0 0 0 / 55%);
        }
      }
    }

    // CAROUSEL_2 移动端箭头：28×38 圆角矩形
    &.carousel-dots {
      :deep(.el-carousel__arrow) {
        width: 28px;
        height: 38px;
        background: rgb(0 0 0 / 35%);
        border-radius: $website-radius-sm;

        &:active {
          background: rgb(0 0 0 / 55%);
        }
      }
    }

    // 移动端指示器：细长条（CAROUSEL_3 默认）
    &.carousel-bars {
      :deep(.el-carousel__indicators) {
        bottom: 2vw;
        display: flex;
        flex-wrap: nowrap;
        justify-content: center;
        max-width: 90%;
        padding: 0 2vw;
        margin: 0 auto;
        overflow-x: auto;
        scrollbar-width: none;

        &::-webkit-scrollbar {
          display: none;
        }

        .el-carousel__indicator {
          flex-shrink: 0;
          margin: 0 4px;

          .el-carousel__button {
            width: 24px;
            height: 3px;
            background: rgb(255 255 255 / 50%);
            border-radius: 2px;
            transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
          }

          &.is-active .el-carousel__button {
            width: 36px;
            height: 4px;
            background: $carousel-color-accent;
          }
        }
      }
    }

    // 移动端指示器：小圆点（CAROUSEL_2）
    &.carousel-dots {
      :deep(.el-carousel__indicators) {
        bottom: 2vw;
        display: flex;
        flex-wrap: nowrap;
        justify-content: center;
        max-width: 90%;
        padding: 0 2vw;
        margin: 0 auto;
        overflow-x: auto;
        scrollbar-width: none;

        &::-webkit-scrollbar {
          display: none;
        }

        .el-carousel__indicator {
          flex-shrink: 0;
          margin: 0 4px;

          .el-carousel__button {
            width: 8px;
            height: 8px;
            background: rgb(255 255 255 / 40%);
            border-radius: 50%;
            transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
          }

          &.is-active .el-carousel__button {
            width: 8px;
            height: 8px;
            background: #fff;
          }
        }
      }
    }

    // 占位背景色
    .el-carousel__item:nth-child(2n) {
      background-color: #f0f2f5;
    }

    .el-carousel__item:nth-child(2n + 1) {
      background-color: #e8eaed;
    }
  }
}
</style>
